import { useState, useEffect, useRef, useCallback } from "react";
import Toast from 'react-native-toast-message';
import usePlatform from "./usePlatform";
import { DateTime } from "luxon";
import { supabase } from "../../data/client"
import { getCurrentOrgID } from "../../data/organization";

const DYMO_STORAGE_KEY = '@dymo_selected_printer';

const useLabel = ({isPrintingAvailable}) => {
    const [printers, setPrinters] = useState([]);
    const [labelXmls, setLabelXmls] = useState({});
    const [labelPreview, setLabelPreview] = useState(null);
    const [selectedPrinter, setSelectedPrinter] = useState(null);
    const [showPrinterSheet, setShowPrinterSheet] = useState(false);
    const [isDiscovering, setIsDiscovering] = useState(false);
    const childLabelRef = useRef(null);
    const familyLabelRef = useRef(null);
    const intervalRef = useRef(null);
    const [childLabelData, setChildLabelData] = useState(null);
    const [familyLabelData, setFamilyLabelData] = useState(null);

    const {isWeb} = usePlatform();

    useEffect(() => {
       setupLabelPrinting()
       checkPrinters()

       return () => {
           if(intervalRef.current) clearInterval(intervalRef.current);
       }
    }, [isPrintingAvailable])


    const setupLabelPrinting = () =>{
        if(!isPrintingAvailable) return;

        if(isWeb){
            //Load the DYMO Connect framework
            const script = document.createElement('script');
            script.src = "https://reachchms.com/dymo.connect.framework.js";
            script.async = true; 
            document.head.appendChild(script);
        }

        const loadPrinters = () => {
            window.dymo.label.framework.init()
            const {isBrowserSupported, isFrameworkInstalled, isWebServicePresent} = window.dymo.label.framework.checkEnvironment();

            
            fetch(`${window.location.origin}/childLabel.dymo`)
                .then((res) => res.text())
                .then((xml) => setLabelXmls(xmls => ({...xmls, child: xml})))
                .catch((err) => console.error("Failed to load child label XML:", err));

            fetch(`${window.location.origin}/familyLabel.dymo`)
                .then((res) => res.text())
                .then((xml) => setLabelXmls(xmls => ({...xmls, parent: xml})))
                .catch((err) => console.error("Failed to load parent label XML:", err));


            if(!isBrowserSupported){
                Toast.show({type: "error", text2: "Your browser does not support DYMO printing"});
                setPrinters([]);
                return;
            }

            if(!isFrameworkInstalled || !isWebServicePresent){
                Toast.show({type: "error", text2: "The DYMO Connect software is not installed on this computer, or the DYMO Connect service is not running. Please install DYMO Connect and run the service to print labels."});
                setPrinters([]);
                return;
            }

            setTimeout(() => {
                const printerList = window.dymo.label.framework.getPrinters();
                setPrinters(printerList);
            }, 1000)
        };
    
        const checkDymoService = () => {
            if (window.dymo && window.dymo.label) {
                loadPrinters();
            }
        };
    
        setTimeout(checkDymoService, 1000);
    }


    const checkPrinters = () => {
        intervalRef.current = setTimeout(() => {
            if(!window.dymo?.label?.framework) return;

            const printerList = window.dymo.label.framework.getPrinters();
            setPrinters(printerList);

            // Mark selected printer disconnected if not in the list
            if (selectedPrinter) {
                const stillConnected = printerList.some(p => p.name === selectedPrinter.name);
                if (!stillConnected) {
                    setSelectedPrinter(prev => prev ? { ...prev, isConnected: false } : null);
                }
            }
        }, 5000)
    }


    const getAge = (dateOfBirth, short = false) => {
        if(!dateOfBirth) return "No Age";

        const today = DateTime.now();
        const dob = DateTime.fromISO(dateOfBirth);
        const ageYears = today.diff(dob, 'years').years;
        const ageMonths = today.diff(dob, 'months').months;
        const ageDays = today.diff(dob, 'days').days;

        return Math.floor(ageYears) > 0 
            ? Math.floor(ageYears).toString() + (short ? " yr" : " year(s) old") 
            : Math.floor(ageMonths) > 0 
                ? Math.floor(ageMonths).toString() + (short ? " mo" : " month(s) old") 
                : Math.floor(ageDays) > 0 
                    ? Math.floor(ageDays).toString() + (short ? " day" : " day(s) old") 
                    : "Age Unknown";
    }


    const getAvatarBase64Image = async (id) => {
        const orgID = await getCurrentOrgID();

        const { data, error } = await supabase
        .storage
        .from(process.env.EXPO_PUBLIC_STORAGE_BUCKET)
        .download(`${orgID}/avatars/${id}.jpg`)
    
        if (error) {
            console.error('Error downloading image:', error)
            return null
        }

        const base64 = await blobToBase64(data);
        return base64.split(',')[1];
    }

    const getLogoBase64Image = async () => {
        const orgID = await getCurrentOrgID();
        const url = `https://mlmibpzqkvhttynlzgpw.supabase.co/storage/v1/object/public/church-app-pub-files/org_logos/${orgID}/logo-long-dark.png`

        const response = await fetch(url)
        const blob = await response.blob()

        const base64 = await blobToBase64(blob);
        return base64.split(',')[1];
    }

    function blobToBase64(blob) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(blob) // This returns a base64 string with MIME type prefix
        })
    }

    const previewChildLabel = async (data) => {
        const base64Label = await printChildLabel(data, true)
        return base64Label
    }

    const previewParentLabel = async (data) => {
        const base64Label = await printParentLabel(data, true)
        return base64Label
    }

    const printChildLabel = async (data, preview = false) => {

        if (!labelXmls['child']) {
            Toast.show({
                type: "error",
                text2: "Could not load the label to print",
            });
            return;
        }

        try {
            const headOfHousehold = data?.Family?.FamilyMembers?.find(f => f?.familyRole == "Head of Household");
            const spouse = data?.Family?.FamilyMembers?.find(f => f?.familyRole == "Spouse");

            let labelText = labelXmls['child'];
            labelText = labelText.replace("{{firstname}}", data?.firstName || "");
            labelText = labelText.replace("{{lastname}}", data?.lastName || "");
            labelText = labelText.replace("{{date}}", DateTime.now().toLocaleString(DateTime.DATE_MED) || "");
            labelText = labelText.replace("{{time}}", DateTime.now().toLocaleString(DateTime.TIME_SIMPLE) || "");
            labelText = labelText.replace("{{customfield1}}", getAge(data?.dateOfBirth) || "");
            labelText = labelText.replace("{{customfield2_line1}}", data?.customFieldValues?.["839e5265-7c95-49f8-b7e9-05cbd15b2855"] || "");
            labelText = labelText.replace("{{customfield2_line2}}", data?.needs || "");
            labelText = labelText.replace("{{customfield3_line1}}", headOfHousehold ? (headOfHousehold?.firstName + " " + headOfHousehold?.lastName) : "");
            labelText = labelText.replace("{{customfield3_line2}}", spouse ? (spouse?.firstName + " " + spouse?.lastName) : "");
            labelText = labelText.replace("{{code}}", data?.code || "");
            labelText = labelText.replace("{{logo_image_base64}}", (await getLogoBase64Image()) || "");
            labelText = labelText.replace("{{avatar_image_base64}}", (await getAvatarBase64Image(data?.id)) || "");

            const label = window.dymo.label.framework.openLabelXml(labelText);

            if(preview){
                const pngData = label.render()
                return "data:image/png;base64," + pngData
            }


            if (printers.filter(p => p.isConnected).length === 0) {
                Toast.show({
                    type: "error",
                    text2: "No printers are connected. Please make sure DYMO Connect is installed and the DYMO printer is connected.",
                });
                return;
            }
            
            label.print(printers[0].name);

            Toast.show({
                type: "info",
                text2: "Printing label...",
            });
        } catch (error) {
            Toast.show({
                type: "error",
                text1: "Failed to print label",
                text2: error.message,
            });
        }
    };

    const printParentLabel = async (data, preview = false) => {
        if (!labelXmls['parent']) {
            Toast.show({
                type: "error",
                text2: "Could not load the label to print",
            });
            return;
        }
    
        try {
            let labelText = labelXmls['parent'];

            for(let i = 0; i < 8; i++) {
                if(data?.members[i]){
                    labelText = labelText.replaceAll(`{{${i}_name}}`, data?.members[i]?.firstName + " " + data?.members[i]?.lastName || "");
                    labelText = labelText.replaceAll(`{{${i}_age}}`, (" - " + getAge(data?.members[i]?.dateOfBirth, true)) || "");
                }
                else{
                    labelText = labelText.replaceAll(`{{${i}_name}}`, "");
                    labelText = labelText.replaceAll(`{{${i}_age}}`, "");
                }
            }

            labelText = labelText.replaceAll("{{date}}", DateTime.fromJSDate(data?.datetime).toLocaleString(DateTime.DATE_MED) || "");
            labelText = labelText.replaceAll("{{time}}", DateTime.fromJSDate(data?.datetime).toLocaleString(DateTime.TIME_SIMPLE) || "");
            labelText = labelText.replaceAll("{{head_1}}", data?.head_1 || "");
            labelText = labelText.replaceAll("{{head_2}}", data?.head_2 || "");
            labelText = labelText.replaceAll("{{code}}", data?.code || "");
            labelText = labelText.replaceAll("{{logo_image_base64}}", (await getLogoBase64Image()) || "");
            
            const label = window.dymo.label.framework.openLabelXml(labelText);

            if(preview){
                const pngData = label.render()
                return "data:image/png;base64," + pngData
            }

            if (printers.filter(p => p.isConnected).length === 0) {
                Toast.show({
                    type: "error",
                    text2: "No printers are connected. Please make sure DYMO Connect is installed and the DYMO printer is connected.",
                });
                return;
            }

            label.print(printers[0].name);

            Toast.show({
                type: "info",
                text2: "Printing label...",
            });
        } catch (error) {
            Toast.show({
                type: "error",
                text1: "Failed to print label",
                text2: error.message,
            });
        }
    };

    const handleSelectPrinter = useCallback((printer) => {
        const printerWithStatus = { ...printer, isConnected: true };
        setSelectedPrinter(printerWithStatus);
        try {
            localStorage.setItem(DYMO_STORAGE_KEY, JSON.stringify(printerWithStatus));
        } catch { /* ignore */ }
        setShowPrinterSheet(false);
    }, []);

    const openPrinterSheet = useCallback(() => {
        setShowPrinterSheet(true);
        // Refresh Dymo printers
        if (window.dymo?.label?.framework) {
            setIsDiscovering(true);
            setTimeout(() => {
                const printerList = window.dymo.label.framework.getPrinters();
                setPrinters(printerList);
                setIsDiscovering(false);
            }, 1000);
        }
    }, []);

    const discoverPrinters = useCallback(() => {
        if (window.dymo?.label?.framework) {
            setIsDiscovering(true);
            setTimeout(() => {
                const printerList = window.dymo.label.framework.getPrinters();
                setPrinters(printerList);
                setIsDiscovering(false);
            }, 1000);
        }
    }, []);

    // Load saved Dymo printer on mount
    useEffect(() => {
        if (!isPrintingAvailable) return;
        try {
            const stored = localStorage.getItem(DYMO_STORAGE_KEY);
            if (stored) {
                const printer = JSON.parse(stored);
                setSelectedPrinter({ ...printer, isConnected: false });
            }
        } catch { /* ignore */ }
    }, [isPrintingAvailable]);

    return {
        printers,
        labelXmls,
        labelPreview,
        selectedPrinter,
        isDiscovering,
        showPrinterSheet,
        setShowPrinterSheet,
        childLabelRef,
        familyLabelRef,
        childLabelData,
        familyLabelData,
        openPrinterSheet,
        handleSelectPrinter,
        connectToDirectWifi: null,
        connectByIP: null,
        discoverPrinters,
        printChildLabel,
        printParentLabel,
        previewChildLabel,
        previewParentLabel,
    }
}

export default useLabel
